import { createRoot } from "react-dom/client";
import App from "./App";
import "./index.css";
import { queryClient } from "@/lib/queryClient";
import { initWebVitals } from "@/lib/analytics";
import { rememberOrganicLanding } from "@/lib/organic-contact-analytics";
import {
  initGlobalErrorReporting,
  initBrokenImageMonitoring,
  reportRecoverableReactError,
} from "@/lib/global-error-reporter";
import { setupServiceWorker } from "./lib/sw-update";

// RUM Core Web Vitals: registra gli observer subito (LCP/CLS richiedono
// osservazione dall'inizio del caricamento); l'invio a GA4 resta comunque
// consent-gated dentro initWebVitals.
initWebVitals();
rememberOrganicLanding();

// Task #289 — errori JS non gestiti (window.onerror / unhandledrejection)
// vengono inviati a /api/client-errors e registrati in system_events.
initGlobalErrorReporting();

// Task #382 — immagini rotte (404 e simili): listener globale in capture
// phase; ogni <img> fallita viene registrata in system_events (source
// "img-404") e oltre 5 per sessione parte un evento escalato a `error`.
initBrokenImageMonitoring();

setupServiceWorker();

// Task #365 — dehydration SSR: il server inietta dentro #root uno script con
// le coppie [queryKey, data] già fetchate lato server per le route catalogo.
// Seminandole in cache PRIMA del primo render, la pagina appare subito con i
// dati reali (niente skeleton flash); eventuali refetch avvengono comunque in
// background senza bloccare il paint.
declare global {
  interface Window {
    __UO_SSR_QUERIES__?: Array<[unknown[], unknown]>;
  }
}
try {
  const seeded = window.__UO_SSR_QUERIES__;
  if (Array.isArray(seeded)) {
    for (const [key, data] of seeded) {
      if (Array.isArray(key) && data !== undefined && data !== null) {
        queryClient.setQueryData(key, data);
      }
    }
    delete window.__UO_SSR_QUERIES__;
  }
} catch {
  // La dehydration è un'ottimizzazione: mai bloccare il boot per un errore qui.
}

const container = document.getElementById("root")!;
// Task #382 — onRecoverableError: React 18 segnala qui gli errori di
// hydration/render recuperabili (mismatch markup server/client). Vengono
// loggati come warn in system_events (source "hydration-error") con route e
// componentStack, visibili in CRM > Salute Sistema.
createRoot(container, {
  onRecoverableError: (error, errorInfo) => {
    reportRecoverableReactError(error, errorInfo);
  },
}).render(<App />);
